import React from 'react';

import { useWelcomeToFormsSnackbar } from 'components/Snackbar';
import { useIsOnboarding } from 'hooks/useIsOnboarding';

import { IntegrationDialog } from './components/IntegrationDialog/IntegrationDialog';
import { NHDUserPreferenceMissingDialog } from './components/NHDUserPreferenceMissingDialog';
import type { StepDialogConfig } from './hooks/types';
import { useStepProgress } from './hooks/useStepProgress';
import { useShowIntegrationsDialog, useShowNHDUserPreferenceMissingDialog } from './hooks';

/**
 * Manages the state of splash dialogs that guide users through setup steps.
 * Each dialog's visibility is controlled by its own hook, which tracks both current visibility
 * and whether it has ever been shown. This allows for automatic progress tracking without
 * explicit "next" callbacks.
 *
 * The order of dialogs in the array determines their sequence - each dialog waits for
 * all previous dialogs to have been shown before it can be considered the "current" step.
 */
function useSplashDialogsState() {
  const integrationsState = useShowIntegrationsDialog();
  const nhdPreferenceState = useShowNHDUserPreferenceMissingDialog();

  const dialogs = React.useMemo<StepDialogConfig[]>(
    () => [
      {
        state: nhdPreferenceState,
        Component: NHDUserPreferenceMissingDialog,
      },
      {
        state: integrationsState,
        Component: IntegrationDialog,
      },
    ],
    [nhdPreferenceState, integrationsState]
  );

  const stepProps = useStepProgress(dialogs);

  return {
    isLoading: React.useMemo(() => dialogs.some(({ state }) => state.isLoading), [dialogs]),
    showAnySplashDialog: React.useMemo(() => dialogs.some(({ state }) => state.show), [dialogs]),
    dialogs,
    stepProps,
  };
}

export function AppSplashDialogs() {
  const isOnboarding = useIsOnboarding();
  const { isLoading, showAnySplashDialog, dialogs, stepProps } = useSplashDialogsState();
  const dialogToShow = React.useMemo(() => dialogs.find(({ state }) => state.show), [dialogs]);

  useWelcomeToFormsSnackbar({ enabled: !isOnboarding && !isLoading && !showAnySplashDialog });

  return !isOnboarding && dialogToShow ? <dialogToShow.Component {...stepProps} /> : null;
}
